import ModalStep from '../enums/modalStep';
import VerificationMethod from '../enums/verificationMethod';

/**
 * Processes an array of verification methods by mapping each method to a `VerificationMethod` instance.
 *
 * @param {Array<Object>} verificationMethods - An array of verification method objects.
 * @param {string} verificationMethods[].Key - The key of the verification method.
 * @returns {Array<VerificationMethod>} An array of `VerificationMethod` instances created from the provided keys.
 */
const processVerificationMethods = (verificationMethods) =>
  verificationMethods.map((verificationMethod) => VerificationMethod.fromKey(verificationMethod.Key));

/**
 * Determines the next step in the verification process based on the available verification methods.
 *
 * @returns {ModalStep} The next modal step:
 * - `ModalStep.Questions` if no verification methods are available.
 * - `ModalStep.PhoneVerification` if phone verification is included in the available methods.
 * - `ModalStep.IdentityVerification` if other verification methods are available.
 */
const getVerificationMethodStep = (verificationMethods) => {
  if (verificationMethods.length === 0) {
    return ModalStep.Questions;
  }

  if (verificationMethods.includes(VerificationMethod.Phone)) {
    return ModalStep.PhoneVerification;
  }

  if (verificationMethods.length === 1 && verificationMethods.includes(VerificationMethod.CreditCard)) {
    return ModalStep.CreditCardVerification;
  }

  return ModalStep.IdentityVerification;
};

/**
 * Determines the next step after phone verification in the process based on the available verification methods.
 *
 * @returns {ModalStep} The next modal step:
 * - `ModalStep.CreditCardVerification` if the only verification method is Credit Card (excluding Phone).
 * - `ModalStep.IdentityVerification` if other verification methods are available.
 */
const getPhoneNextVerificationMethodStep = (verificationMethods) => {
  if (verificationMethods.filter(method => method.name !== VerificationMethod.Phone.name).length === 1 &&
      verificationMethods.includes(VerificationMethod.CreditCard)) {
    return ModalStep.CreditCardVerification;
  }
  return ModalStep.IdentityVerification;
};

export { processVerificationMethods, getVerificationMethodStep, getPhoneNextVerificationMethodStep };
